<template>
  <div>
    <Breadcrumbs :items="breadcrumbs" page_title="live" />

    <v-container fluid class="live-report-page px-4 px-md-6 py-6">
      <v-alert
        v-if="loadError"
        dense
        outlined
        type="error"
        class="mb-4 rounded-xl"
      >
        {{ loadError }}
      </v-alert>

      <v-alert
        v-if="infoNotice"
        dense
        text
        type="info"
        class="mb-4 rounded-xl"
      >
        {{ infoNotice }}
      </v-alert>

      <v-card class="pa-4 mb-4 rounded-xl" flat>
        <div class="report-header">
          <div>
            <div class="report-header__eyebrow">{{ $t("livefeature_live_session_report") }}</div>
            <div class="report-header__title">{{ reportTitle }}</div>
            <div class="report-header__subtitle">{{ reportSubtitle }}</div>
          </div>

          <div class="report-header__status">
            <v-chip small label :color="statusChipColor" text-color="white">
              {{ sessionStatusLabel }}
            </v-chip>
          </div>
        </div>

        <div class="report-header__actions mt-4">
          <v-btn text color="#475569" class="text-capitalize mr-2" @click="handleExportExcel">
            <v-icon left small>mdi-file-excel</v-icon>
            {{ $t("livefeature_export_excel") }}
          </v-btn>
        </div>
      </v-card>

      <v-row class="mb-4">
        <v-col
          v-for="metric in metrics"
          :key="metric.label"
          cols="12"
          sm="6"
          md="3"
        >
          <v-card flat class="metric-card rounded-xl text-center">
            <div class="metric-card__value" :style="{ color: metric.color }">
              {{ metric.value }}
            </div>
            <div class="metric-card__label">{{ metric.label }}</div>
            <div v-if="metric.helper" class="metric-card__helper">{{ metric.helper }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-card flat class="recording-card rounded-xl pa-4 pa-md-5 mb-4">
        <div class="table-card__title mb-4">
          <v-icon left color="#0f766e">mdi-video-outline</v-icon>
          Recordings ({{ displayRecordings.length }})
        </div>

        <!-- B03 (F5) — โหลดสถานะไลบรารี่ไม่สำเร็จ (เทา — ข้อมูลรายงานหลักยังถูกต้อง) -->
        <div v-if="libraryStatusError" class="report-rec-status-unavailable mb-3" data-testid="report-rec-status-unavailable">
          <v-icon x-small color="#94a3b8" class="mr-1">mdi-information-outline</v-icon>{{ $t('livefeature_recording_status_unavailable') }}
        </div>
        <!-- B03 (D26) — ผลการกดลองใหม่ -->
        <v-alert
          v-if="recordingNotice"
          :type="recordingNotice.type"
          dense
          :outlined="recordingNotice.type === 'error'"
          :text="recordingNotice.type === 'info'"
          class="mb-4 rounded-xl"
          data-testid="report-rec-notice"
        >{{ recordingNotice.text }}</v-alert>

        <div v-if="loading" class="text-center pa-6">
          <v-progress-circular indeterminate color="#16a34a" />
        </div>

        <div v-else-if="!displayRecordings.length" class="report-empty pa-8 text-center">
          <v-icon size="48" color="#cbd5e1" class="mb-3">mdi-video-off-outline</v-icon>
          <div class="report-empty__title mb-2">No recordings yet</div>
          <div class="report-empty__copy">Recordings will appear here after the session ends and files are uploaded.</div>
        </div>

        <v-list v-else two-line class="pa-0">
          <v-list-item
            v-for="(rec, index) in displayRecordings"
            :key="rec.id || index"
            class="recording-list-item mb-2 rounded-xl"
            data-testid="report-rec-row"
            :data-record-id="rec.id"
          >
            <v-list-item-avatar tile size="64" class="recording-thumb mr-4" color="#111827" rounded="lg">
              <v-icon color="white" size="28">mdi-video</v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="recording-list-item__name font-weight-bold">
                {{ rec.statusOnly ? rec.title : (rec.fileName || `Recording ${index + 1}`) }}
              </v-list-item-title>
              <v-list-item-subtitle class="mt-1">
                <span class="mr-4">
                  <v-icon x-small color="#64748b" class="mr-1">mdi-clock-outline</v-icon>
                  {{ rec.duration || '--' }}
                </span>
                <span class="mr-4">
                  <v-icon x-small color="#64748b" class="mr-1">mdi-calendar</v-icon>
                  {{ formatRecordingDate(rec.createdDate) }}
                </span>
                <span v-if="rec.fileSizeMB">
                  <v-icon x-small color="#64748b" class="mr-1">mdi-harddisk</v-icon>
                  {{ rec.fileSizeMB }} MB
                </span>
              </v-list-item-subtitle>
              <!-- B03 (F5) — สถานะไลบรารี่ (นอก subtitle ที่ตัดบรรทัดเดียว — E59) · ไม่มี wrapper เมื่อแถว eligible false (Plan C4) -->
              <div
                v-if="(libraryStatusLoading && !libraryStatusLoaded) || (libraryStatusById[rec.id] && libraryStatusById[rec.id].eligible !== false)"
                class="report-rec-row__library mt-2"
              >
                <v-progress-circular
                  v-if="libraryStatusLoading && !libraryStatusLoaded"
                  indeterminate
                  size="16"
                  width="2"
                  color="#16a34a"
                  data-testid="rec-status-loading"
                />
                <LiveRecordingLibraryBadge
                  v-else-if="libraryStatusById[rec.id]"
                  :item="libraryStatusById[rec.id]"
                  small
                  session-ended
                  show-lesson-name
                  :can-retry="check_Permissions('live')"
                  :retrying="retryingRecordId === rec.id"
                  :disabled="isRetryingImport"
                  @retry="retryRecordingImport"
                />
              </div>
            </v-list-item-content>

            <v-list-item-action v-if="!rec.statusOnly" class="d-flex flex-row align-center" style="gap:8px">
              <v-btn
                small
                color="#16a34a"
                dark
                class="text-capitalize"
                :disabled="!(rec.id || rec.egressId || rec.filePath)"
                @click="openPlayer(rec)"
              >
                <v-icon left small>mdi-play</v-icon>
                Watch
              </v-btn>
              <v-btn
                small
                outlined
                color="#475569"
                class="text-capitalize"
                :disabled="!rec.playbackUrl"
                :href="rec.playbackUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-icon left small>mdi-download</v-icon>
                Download
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>

      <!-- Video Player Dialog -->
      <v-dialog v-model="playerDialog" max-width="900" persistent>
        <v-card class="rounded-xl" color="#0f172a">
          <v-card-title class="d-flex justify-space-between align-center pa-4">
            <span class="white--text text-subtitle-1 font-weight-bold text-truncate" style="max-width:80%">
              {{ activeRecording && activeRecording.fileName || 'Recording' }}
            </span>
            <v-btn icon color="white" @click="closePlayer">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <div class="px-4 pb-4">
            <div v-if="playerLoading" class="text-center pa-8">
              <v-progress-circular indeterminate color="#16a34a" size="48" />
              <div class="white--text mt-4 text-body-2">Fetching playback link…</div>
            </div>
            <div v-else-if="playerError" class="text-center white--text pa-8">
              <v-icon size="48" color="#ef4444" class="mb-3">mdi-alert-circle-outline</v-icon>
              <div class="mb-2">{{ playerError }}</div>
              <div class="text-caption" style="color:#94a3b8">Ask your backend team to add a <code>GetRecordingDownloadUrl</code> endpoint or make the blob container public.</div>
            </div>
            <video
              v-else-if="activeRecording && (activeRecording.resolvedUrl || activeRecording.playbackUrl)"
              ref="reportVideoEl"
              :src="activeRecording.resolvedUrl || activeRecording.playbackUrl"
              controls
              autoplay
              style="width:100%;border-radius:12px;background:#000;max-height:520px;"
            ></video>
            <div v-else class="text-center white--text pa-8">
              <v-icon size="48" color="#475569" class="mb-3">mdi-link-off</v-icon>
              <div>No playback URL available for this recording.</div>
              <div class="text-caption mt-2" style="color:#94a3b8">The backend needs to expose a signed/SAS URL for this file.</div>
            </div>

            <div v-if="activeRecording" class="mt-3 d-flex align-center justify-space-between">
              <span class="white--text text-caption">
                <v-icon x-small color="#94a3b8" class="mr-1">mdi-clock-outline</v-icon>
                {{ activeRecording.duration || '--' }}
                <span v-if="activeRecording.fileSizeMB" class="ml-4">
                  <v-icon x-small color="#94a3b8" class="mr-1">mdi-harddisk</v-icon>
                  {{ activeRecording.fileSizeMB }} MB
                </span>
              </span>
              <v-btn
                small
                outlined
                color="white"
                class="text-capitalize"
                :href="activeRecording.resolvedUrl || activeRecording.playbackUrl"
                :disabled="!(activeRecording.resolvedUrl || activeRecording.playbackUrl)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-icon left small>mdi-download</v-icon>
                Download
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <v-card flat class="table-card rounded-xl pa-4 pa-md-5 mb-4">
        <div class="table-card__title mb-4">
          <v-icon left color="#0f766e">mdi-account-multiple-outline</v-icon>
          {{ attendeeCountLabel }}
        </div>

        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="attendees"
          :search="search"
          :items-per-page="10"
          hide-default-footer
          class="attendee-table"
        >
          <template v-slot:top>
            <div class="table-card__toolbar mb-4">
              <v-text-field
                v-model="search"
                outlined
                dense
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                :placeholder="$t('livefeature_search_attendees')"
                class="table-card__search"
              />
            </div>
          </template>

          <template v-slot:item.fullName="{ item }">
            <div class="attendee-name">{{ item.fullName }}</div>
          </template>

          <template v-slot:item.engagement="{ item }">
            <div class="engagement-stack">
              <span class="engagement-pill">
                <v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>
                {{ item.engagement.chat }}
              </span>
              <span class="engagement-pill">
                <v-icon x-small class="mr-1">mdi-hand-back-left</v-icon>
                {{ item.engagement.hand }}
              </span>
              <span class="engagement-pill">
                <v-icon x-small class="mr-1">mdi-hand-clap</v-icon>
                {{ item.engagement.clap }}
              </span>
              <span class="engagement-pill">
                <v-icon x-small class="mr-1">mdi-heart</v-icon>
                {{ item.engagement.heart }}
              </span>
              <span class="engagement-pill">
                <v-icon x-small class="mr-1">mdi-thumb-up</v-icon>
                {{ item.engagement.like }}
              </span>
              <span class="engagement-pill">
                <v-icon x-small class="mr-1">mdi-emoticon-lol-outline</v-icon>
                {{ item.engagement.funny }}
              </span>
              <span class="engagement-pill">
                <v-icon x-small class="mr-1">mdi-emoticon-confused-outline</v-icon>
                {{ item.engagement.confused }}
              </span>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip small label :class="statusClass(item.status)">
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="report-empty pa-8 text-center">
              <div class="report-empty__title mb-2">{{ $t("livefeature_no_attendee_data") }}</div>
              <div class="report-empty__copy">
                {{ $t("livefeature_no_attendee_data_copy") }}
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card>


    </v-container>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import LiveRecordingLibraryBadge from "@/components/LiveRecordingLibraryBadge.vue"; // B03

export default {
  name: "LiveReport",
  components: { LiveRecordingLibraryBadge }, // B03

  data() {
    return {
      loading: true,
      search: "",
      loadError: "",
      infoNotice: "",
      sessionDetail: null,
      recordings: [],
      playerDialog: false,
      activeRecording: null,
      playerLoading: false,
      playerError: "",
      attendees: [],
      // B03 (F5) — สถานะไลบรารี่ของการบันทึก + ปุ่มลองใหม่
      libraryStatusList: [],
      libraryStatusLoading: false,
      libraryStatusLoaded: false,
      libraryStatusError: false,
      recordingNotice: null, // { type: "info" | "error", text }
      isRetryingImport: false,
      retryingRecordId: "",
      libraryRefreshTimer: null,
      libraryRefreshCount: 0,
    };
  },

  computed: {
    sessionId() {
      return this.$route.params.roomId;
    },

    reportTitle() {
      return (
        this.sessionDetail?.title ||
        this.$route.query.title ||
        `${this.$t("livefeature_live_session_prefix")} #${this.sessionId}`
      );
    },

    reportSubtitle() {
      const host = this.sessionDetail?.hostName || this.sessionDetail?.hostUserName || this.$route.query.host || this.$t("livefeature_host_pending");
      const duration = this.$route.query.duration || this.reportDurationLabel;
      return `${this.$t("live")} / ${this.$t("livefeature_report")} / ${this.$t("livefeature_host_label")}: ${host} / ${duration}`;
    },

    sessionStatusLabel() {
      return (
        this.sessionDetail?.statusName ||
        this.$route.query.status ||
        this.$t("livefeature_ended")
      );
    },

    statusChipColor() {
      const normalized = String(this.sessionStatusLabel || "").toLowerCase();
      if (normalized.includes("live")) {
        return "#dc2626";
      }
      if (normalized.includes("schedule")) {
        return "#1976d2";
      }
      return "#64748b";
    },

    registeredCount() {
      const fromRoute = parseInt(this.$route.query.registered, 10);
      return Number.isNaN(fromRoute) ? this.attendees.length : fromRoute;
    },

    joinedCount() {
      return this.attendees.filter((attendee) => attendee.joinedFlag).length;
    },

    invitedCount() {
      return this.attendees.filter((attendee) => attendee.status === this.$t("livefeature_status_invited")).length;
    },

    reportDurationSeconds() {
      const startTime = this.sessionDetail?.startTime;
      const endTime = this.sessionDetail?.endTime;

      if (!startTime || !endTime) {
        return 0;
      }

      const start = new Date(startTime);
      const end = new Date(endTime);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return 0;
      }

      return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
    },

    reportDurationLabel() {
      return this.formatDuration(this.reportDurationSeconds);
    },

    attendeeCountLabel() {
      return `${this.$t("livefeature_attendee_list")} (${this.attendees.length} ${this.$t("livefeature_people")})`;
    },

    attendancePercentLabel() {
      if (!this.registeredCount) {
        return "0%";
      }

      return `${Math.round((this.joinedCount / this.registeredCount) * 100)}%`;
    },

    sessionRecordingsEndpoint() {
      return `${this.web_url}LiveSession/GetLiveReportByID/${this.sessionId}`;
    },

    // B03 (F5) — สถานะไลบรารี่ / ปุ่มลองใหม่ (v1, ผู้เรียกจาก token — D21)
    recordingLibraryStatusEndpoint() {
      return `${this.web_url}LiveSession/GetRecordingLibraryStatus/${this.sessionId}`;
    },

    recordingImportEndpoint() {
      return `${this.web_url}LiveSession/ImportRecordingsToLibrary/${this.sessionId}`;
    },

    // B03 (F5) — key = liveSessionRecordID (= rec.id ของแถว report)
    libraryStatusById() {
      return this.libraryStatusList.reduce((map, item) => {
        if (item && item.liveSessionRecordID) map[item.liveSessionRecordID] = item;
        return map;
      }, {});
    },

    // B03 (F5, D31) — แถว report เดิม + แถวที่มีใน status แต่ไม่มีใน report (Status 0/2, eligible !== false)
    displayRecordings() {
      const reportIds = new Set(this.recordings.map((rec) => rec.id));
      const extra = [];
      this.libraryStatusList.forEach((item, index) => {
        if (!item || reportIds.has(item.liveSessionRecordID)) return;
        if (item.eligible === false) return;
        if (item.recordStatus !== 0 && item.recordStatus !== 2) return;
        extra.push({
          id: item.liveSessionRecordID,
          statusOnly: true,
          title: this.$t("livefeature_recording_item", { n: index + 1 }),
          duration: item.duration || "--",
          createdDate: item.createdDate,
          fileSizeMB: item.fileSizeMB,
        });
      });
      return this.recordings.concat(extra);
    },

    // B03 (F5, D31) — เงื่อนไขเดียวกับ Host: แถว eligible === false ไม่ทำให้ refresh
    hasProcessingRecording() {
      return this.libraryStatusList.some((r) => r.eligible !== false &&
        (r.recordStatus === 0 || (r.recordStatus === 1 && [null, undefined, 0, 1].includes(r.libraryStatus))));
    },

    metrics() {
      return [
        {
          value: String(this.joinedCount),
          label: this.$t("livefeature_actual_attendees"),
          color: "#16a34a",
          helper: `${this.invitedCount} ${this.$t("livefeature_invited_not_joined")}`,
        },
        {
          value: this.attendancePercentLabel,
          label: this.$t("livefeature_attendance_registered"),
          color: "#16a34a",
          helper: `${this.registeredCount} ${this.$t("livefeature_registered_count")}`,
        },
        {
          value: this.reportDurationLabel,
          label: this.$t("livefeature_total_duration"),
          color: "#16a34a",
          helper: this.reportDurationSeconds ? this.$t("livefeature_based_on_scheduled_time") : this.$t("livefeature_duration_api_pending"),
        },
        {
          value: "--",
          label: this.$t("livefeature_average_rating"),
          color: "#ca8a04",
          helper: this.$t("livefeature_rating_api_pending"),
        },
      ];
    },

    headers() {
      return [
        { text: this.$t("livefeature_name"), value: "fullName", sortable: false, class: "tableColor" },
        { text: this.$t("livefeature_invited"), value: "invitedDate", sortable: false, class: "tableColor" },
        { text: this.$t("livefeature_joined"), value: "joinedDate", sortable: false, class: "tableColor" },
        { text: this.$t("livefeature_total_time"), value: "totalTime", sortable: false, class: "tableColor" },
        { text: this.$t("livefeature_attendance_percent"), value: "attendance", sortable: false, class: "tableColor" },
        { text: this.$t("livefeature_engagement"), value: "engagement", sortable: false, class: "tableColor" },
        { text: this.$t("livefeature_status"), value: "status", sortable: false, class: "tableColor" },
      ];
    },

    breadcrumbs() {
      return [
        { sidebar_tilte: "home" },
        { text: "live", href: "/home/live" },
        { text: this.reportTitle },
        { text: this.$t("livefeature_report") },
      ];
    },
  },

  async created() {
    await this.initializeReport();
  },

  beforeDestroy() {
    this.stopLibraryRefresh(); // B03
  },

  methods: {
    async initializeReport() {
      this.loading = true;
      this.loadError = "";
      this.infoNotice = "";

      try {
        await this.fetchLiveReport();
      } catch (error) {
        this.loadError =
          error.response?.data?.message ||
          error.message ||
          this.$t("livefeature_unable_load_live_report");
      } finally {
        this.loading = false;
      }

      // B03 (F5) — แยกจากรายงานหลัก: ล้มไม่ตั้ง loadError (method จับ error เอง — Plan C15)
      await this.fetchRecordingLibraryStatus();
    },

    async fetchLiveReport() {
      const response = await this.$axios.get(this.sessionRecordingsEndpoint, {
        headers: { accept: "*/*" },
      });

      const report = response.data?.data || response.data;
      if (!report || typeof report !== "object") return;

      // Session detail — used by header, metrics, duration
      this.sessionDetail = report;

      // Attendees from liveReportUsers
      const users = Array.isArray(report.liveReportUsers) ? report.liveReportUsers : [];
      this.attendees = users.map((u, index) => {
        const joinedFlag = Boolean(u.attendanceIn);
        return {
          id: u.userID || index + 1,
          fullName: u.fullName || `${this.$t("livefeature_participants_label")} ${index + 1}`,
          invitedDate: "-",
          joinedDate: this.formatParticipantDate(u.attendanceIn),
          totalTime: this.parseTotalTime(u.totalTime),
          attendance: u.attendance || (joinedFlag ? "100%" : "0%"),
          engagement: {
            chat: u.chatCount || 0,
            hand: u.handCount || 0,
            clap: u.clapCount || 0,
            heart: u.heartCount || 0,
            like: u.likeCount || 0,
            funny: u.funnyCount || 0,
            confused: u.confusedCount || 0,
          },
          joinedFlag,
          status: u.status || (joinedFlag ? this.$t("livefeature_status_joined") : this.$t("livefeature_status_pending")),
        };
      });

      // Recordings from liveSessionRecords — filePath is already a full SAS URL
      const records = Array.isArray(report.liveSessionRecords) ? report.liveSessionRecords : [];
      this.recordings = records.map((rec) => ({
        id: rec.liveSessionRecordID || rec.id,
        egressId: "",
        fileName: rec.fileName || String(rec.filePath || "").split("/").pop() || "recording.mp4",
        filePath: rec.filePath || "",
        duration: this.parseTotalTime(rec.duration),
        fileSizeMB: rec.fileSizeMB || null,
        createdDate: report.startTime || "",
        status: null,
        playbackUrl: rec.filePath || "",
      }));
    },

    // Parses "0001-01-01T00:01:06.527" or "H:MM:SS" duration strings
    parseTotalTime(value) {
      if (!value) return "--";
      const timePart = String(value).includes("T") ? String(value).split("T")[1] : String(value);
      const segments = timePart.split(":");
      if (segments.length < 3) return "--";
      const h = parseInt(segments[0], 10);
      const m = segments[1].padStart(2, "0");
      const s = String(Math.floor(parseFloat(segments[2]))).padStart(2, "0");
      return `${h}:${m}:${s}`;
    },

    formatParticipantDate(value) {
      if (!value) {
        return "-";
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return "-";
      }

      return parsedDate.toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatDuration(totalSeconds) {
      const value = Math.max(0, Number(totalSeconds || 0));
      const hours = Math.floor(value / 3600)
        .toString()
        .padStart(1, "0");
      const minutes = Math.floor((value % 3600) / 60)
        .toString()
        .padStart(2, "0");
      const seconds = Math.floor(value % 60)
        .toString()
        .padStart(2, "0");

      return `${hours}:${minutes}:${seconds}`;
    },

    formatRecordingDuration(totalSeconds) {
      const value = Number(totalSeconds || 0);

      if (!value || Number.isNaN(value)) {
        return "";
      }

      return this.formatDuration(value);
    },

    formatRecordingDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("en-US", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    },

    // B03 (F5) — status ล้ม = libraryStatusError (ไม่ตั้ง loadError — หน้า Report เดิมต้องใช้ได้)
    async fetchRecordingLibraryStatus({ silent = false } = {}) {
      this.libraryStatusLoading = !silent && !this.libraryStatusLoaded;
      try {
        const response = await this.$axios.get(this.recordingLibraryStatusEndpoint, {
          headers: { accept: "*/*" },
        });
        if (response.data?.status !== 0) throw new Error("GetRecordingLibraryStatus failed");
        this.libraryStatusList = Array.isArray(response.data.data) ? response.data.data : [];
        this.libraryStatusLoaded = true;
        this.libraryStatusError = false;
        await this.refetchReportIfNeeded(); // Plan C3
        if (this.hasProcessingRecording) this.startLibraryRefresh();
      } catch (error) {
        if (!this.libraryStatusLoaded) this.libraryStatusError = true; // refresh ล้มหลังเคยโหลดสำเร็จ → เงียบ (Plan C9)
        console.warn("[Report] GetRecordingLibraryStatus:", error.response?.status, error.message);
      } finally {
        this.libraryStatusLoading = false;
      }
    },

    // B03 (Plan C3) — record ที่เป็น Status 1 / นำเข้าแล้ว แต่ยังไม่อยู่ใน report (GetLiveReportByID คืนเฉพาะ Status 1)
    // → โหลด report ซ้ำ 1 ครั้งต่อ id (กันวน) · force = หลังกดลองใหม่ (spec: แถว Imported ที่ไม่มีใน report)
    async refetchReportIfNeeded({ force = false } = {}) {
      if (this.loadError) return; // รายงานหลักโหลดไม่สำเร็จ — ไม่โหลดซ้ำเงียบ ๆ
      if (!this.reportRefetchedIds) this.reportRefetchedIds = new Set();
      const reportIds = new Set(this.recordings.map((rec) => rec.id));
      const missing = this.libraryStatusList
        .filter((r) => r && (r.recordStatus === 1 || r.libraryStatus === 2) && !reportIds.has(r.liveSessionRecordID))
        .map((r) => r.liveSessionRecordID)
        .filter((id) => force || !this.reportRefetchedIds.has(id));
      if (!missing.length) return;
      missing.forEach((id) => this.reportRefetchedIds.add(id));
      try {
        await this.fetchLiveReport();
      } catch (error) {
        console.warn("[Report] refetch GetLiveReportByID:", error.response?.status, error.message);
      }
    },

    // B03 (D26) — POST ทั้ง session ≤ 60 วิ (K19) · กันกดซ้ำ
    async retryRecordingImport(item) {
      if (this.isRetryingImport || !item) return;
      this.isRetryingImport = true;
      this.retryingRecordId = item.liveSessionRecordID;
      this.recordingNotice = null;
      try {
        const response = await this.$axios.post(this.recordingImportEndpoint, null, {
          headers: { accept: "*/*" },
        });
        if (response.data?.status !== 0) throw new Error("ImportRecordingsToLibrary failed");
        this.libraryStatusList = Array.isArray(response.data.data) ? response.data.data : [];
        this.libraryStatusLoaded = true;
        this.libraryStatusError = false;
        const same = this.libraryStatusList.find((r) => r.liveSessionRecordID === item.liveSessionRecordID);
        if (same && same.libraryStatus === 3) {
          this.recordingNotice = { type: "info", text: this.$t("livefeature_recording_retry_still_failed") };
        }
        // spec: แถวเปลี่ยนเป็น Imported ที่ไม่มีใน report → fetchLiveReport() ซ้ำ (record ที่ reconcile เป็น Status 1 ได้ปุ่ม Watch)
        await this.refetchReportIfNeeded({ force: true });
        if (this.hasProcessingRecording) this.startLibraryRefresh();
      } catch (error) {
        console.warn("[Report] ImportRecordingsToLibrary:", error.response?.status, error.message);
        this.recordingNotice = {
          type: "error",
          text: error.response?.status === 403
            ? this.$t("livefeature_recording_no_permission")
            : this.$t("livefeature_recording_retry_failed"),
        };
      } finally {
        this.isRetryingImport = false;
        this.retryingRecordId = "";
      }
    },

    // B03 (F5) — refresh เงียบทุก 30 วิ ≤ 20 ครั้ง เฉพาะเมื่อมีแถวกำลังประมวลผล
    startLibraryRefresh() {
      if (this._isBeingDestroyed || this._isDestroyed) return; // created() async (Plan C2)
      if (this.libraryRefreshTimer) return;
      this.libraryRefreshCount = 0;
      this.libraryRefreshTimer = setInterval(() => {
        if (this._isBeingDestroyed || this._isDestroyed) {
          this.stopLibraryRefresh();
          return;
        }
        if (this.isRetryingImport) return; // ไม่ทับผลของปุ่มลองใหม่ (Plan C8)
        if (!this.hasProcessingRecording || this.libraryRefreshCount >= 20) {
          this.stopLibraryRefresh();
          return;
        }
        this.libraryRefreshCount += 1;
        this.fetchRecordingLibraryStatus({ silent: true });
      }, 30000);
    },

    stopLibraryRefresh() {
      if (this.libraryRefreshTimer) clearInterval(this.libraryRefreshTimer);
      this.libraryRefreshTimer = null;
    },

    async openPlayer(rec) {
      this.activeRecording = { ...rec };
      this.playerDialog = true;
      this.playerLoading = false;
      this.playerError = "";

      if (!rec.playbackUrl) {
        this.playerError = "No playback URL available for this recording.";
      }
    },

    closePlayer() {
      this.playerDialog = false;
      this.playerLoading = false;
      this.playerError = "";
      this.$nextTick(() => {
        const vid = this.$refs.reportVideoEl;
        if (vid) { vid.pause(); vid.src = ""; }
        this.activeRecording = null;
      });
    },

    async handleExportExcel() {
      const workbook = new ExcelJS.Workbook();

      const applySheetStyle = (worksheet) => {
        // Center all populated/empty cells to keep a consistent production layout.
        worksheet.eachRow({ includeEmpty: true }, (row) => {
          row.eachCell({ includeEmpty: true }, (cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          });
        });

        const header = worksheet.getRow(1);
        header.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE67E22" },
          };
          cell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
          };
        });

        // Auto-fit each column width based on max text length plus padding.
        worksheet.columns.forEach((column) => {
          let maxLength = 0;
          column.eachCell({ includeEmpty: true }, (cell) => {
            const rawValue = cell.value;
            let value = "";

            if (rawValue == null) {
              value = "";
            } else if (typeof rawValue === "string" || typeof rawValue === "number" || typeof rawValue === "boolean") {
              value = String(rawValue);
            } else if (rawValue.text) {
              value = String(rawValue.text);
            } else if (rawValue.richText && Array.isArray(rawValue.richText)) {
              value = rawValue.richText.map((item) => item.text || "").join("");
            } else if (rawValue.hyperlink) {
              value = String(rawValue.text || rawValue.hyperlink);
            } else if (rawValue.formula) {
              value = String(rawValue.result || rawValue.formula);
            } else {
              value = String(rawValue);
            }

            maxLength = Math.max(maxLength, value.length);
          });
          column.width = Math.max(12, maxLength + 4);
        });
      };

      const attendeeHeaders = [
        "Name",
        "Invited Date",
        "Joined Date",
        "Total Time",
        "Attendance %",
        "Chat",
        "Hand",
        "Clap",
        "Heart",
        "Like",
        "Funny",
        "Confused",
        "Status",
      ];

      const attendeeSheet = workbook.addWorksheet("Attendees", {
        views: [{ showGridLines: true }],
      });
      attendeeSheet.addRow(attendeeHeaders);
      if (this.attendees.length) {
        this.attendees.forEach((a) => {
          attendeeSheet.addRow([
            a.fullName,
            a.invitedDate,
            a.joinedDate,
            a.totalTime,
            a.attendance,
            a.engagement.chat,
            a.engagement.hand,
            a.engagement.clap,
            a.engagement.heart,
            a.engagement.like,
            a.engagement.funny,
            a.engagement.confused,
            a.status,
          ]);
        });
      } else {
        attendeeSheet.addRow(attendeeHeaders.map(() => ""));
      }
      applySheetStyle(attendeeSheet);

      const recordingHeaders = [
        "#",
        "File Name",
        "Duration",
        "File Size (MB)",
        "Created Date",
        "Download URL",
      ];

      const recordingSheet = workbook.addWorksheet("Recordings", {
        views: [{ showGridLines: true }],
      });
      recordingSheet.addRow(recordingHeaders);
      if (this.recordings.length) {
        this.recordings.forEach((r, i) => {
          recordingSheet.addRow([
            i + 1,
            r.fileName,
            r.duration,
            r.fileSizeMB || "",
            this.formatRecordingDate(r.createdDate),
            r.playbackUrl || "",
          ]);
        });
      } else {
        recordingSheet.addRow(recordingHeaders.map(() => ""));
      }
      applySheetStyle(recordingSheet);

      const safeTitle = String(this.reportTitle || "LiveReport").replace(/[^a-zA-Z0-9_\- ]/g, "").trim() || "LiveReport";
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      saveAs(blob, `${safeTitle}_Report.xlsx`);
    },

    handleOpenLibrary() {
      this.$router.push("/home/library");
    },

    statusClass(status) {
      if (status === this.$t("livefeature_status_joined")) {
        return "status-chip status-chip--passed";
      }

      if (status === this.$t("livefeature_status_invited")) {
        return "status-chip status-chip--invited";
      }

      return "status-chip status-chip--failed";
    },
  },
};
</script>

<style scoped>
.live-report-page {
  background: linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%);
  min-height: 100%;
}

.report-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
}

.report-header__eyebrow {
  color: #16a34a;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.report-header__title {
  color: #1f2937;
  font-size: 1.65rem;
  font-weight: 700;
  margin-top: 6px;
}

.report-header__subtitle {
  color: #64748b;
  font-size: 0.96rem;
  margin-top: 6px;
}

.report-header__actions {
  display: flex;
  flex-wrap: wrap;
}

.report-header__status {
  margin-left: 16px;
}

.metric-card,
.recording-card,
.table-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.06);
}

.metric-card {
  padding: 26px 18px;
}

.metric-card__value {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
}

.metric-card__label {
  color: #64748b;
  font-size: 0.92rem;
  margin-top: 12px;
}

.metric-card__helper {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-top: 8px;
}

.recording-preview {
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  border-radius: 18px;
  height: 100%;
  min-height: 200px;
  position: relative;
}

.recording-preview__overlay {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: relative;
}

.recording-preview__play {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.24);
}

.recording-preview__time {
  background: rgba(15, 23, 42, 0.72);
  border-radius: 999px;
  bottom: 16px;
  color: #ffffff;
  font-size: 0.78rem;
  padding: 6px 10px;
  position: absolute;
  right: 16px;
}

.recording-card__title {
  align-items: center;
  color: #0f172a;
  display: flex;
  font-size: 1.08rem;
  font-weight: 700;
}

.recording-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 22px;
}

.recording-meta__item {
  align-items: center;
  color: #475569;
  display: flex;
  font-size: 0.92rem;
}

.recording-chip {
  border-radius: 999px;
  font-weight: 600;
}

.recording-chip--green {
  background: #dcfce7 !important;
  color: #166534 !important;
}

.recording-chip--amber {
  background: #fef3c7 !important;
  color: #92400e !important;
}

.table-card__title {
  align-items: center;
  color: #334155;
  display: flex;
  font-size: 1.04rem;
  font-weight: 700;
}

.table-card__toolbar {
  display: flex;
  justify-content: flex-end;
}

.table-card__search {
  max-width: 320px;
}

.attendee-table::v-deep table {
  width: 100%;
}

.attendee-table::v-deep thead th {
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.attendee-table::v-deep tbody td {
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  color: #334155;
  font-size: 0.93rem;
  padding-bottom: 14px;
  padding-top: 14px;
}

.attendee-name {
  color: #0f172a;
  font-weight: 600;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.engagement-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.engagement-pill {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 999px;
  color: #475569;
  display: inline-flex;
  font-size: 0.82rem;
  padding: 4px 8px;
}

.status-chip {
  border-radius: 999px;
  font-weight: 700;
}

.status-chip--passed {
  background: #dcfce7 !important;
  color: #166534 !important;
}

.status-chip--invited {
  background: #dbeafe !important;
  color: #1d4ed8 !important;
}

.status-chip--failed,
.status-chip--absent {
  background: #fee2e2 !important;
  color: #b91c1c !important;
}

.report-empty__title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
}

.report-empty__copy {
  color: #64748b;
  font-size: 0.95rem;
}

/* B03 (F5) — สถานะไลบรารี่ของแต่ละแถว / โหลดสถานะไม่สำเร็จ */
.report-rec-row__library {
  white-space: normal;
}

.report-rec-status-unavailable {
  color: #64748b;
  font-size: 0.85rem;
}

@media (max-width: 960px) {
  .report-header {
    flex-direction: column;
    gap: 12px;
  }

  .report-header__status {
    margin-left: 0;
  }

  .table-card__toolbar {
    justify-content: flex-start;
  }
}
</style>